// Связь с тостами: типы и контекст ToastProvider. Отдельно от Toast.tsx — тост показывает
// OperationCode, а OperationCode сам показывает тост «Номер скопирован».
import { createContext, useContext } from 'react';

export type ToastKind = 'ok' | 'error' | 'info';

/** Время жизни: 'normal' — 4 с, 'short' — 2 с («Ссылка скопирована»), 'long' — 6 с. */
export type ToastDuration = 'short' | 'normal' | 'long';

export interface ToastAction {
  /** «Отменить», «Открыть», «Повторить». */
  label: string;
  onClick: () => void;
}

export interface ToastOptions {
  kind?: ToastKind;
  action?: ToastAction;
  duration?: ToastDuration | number;
  /** Код ошибки после текста: «Код ошибки REC-008». */
  code?: string;
  /**
   * Номер операции из ответа сервера (FR-028): строкой ниже «Код SYS-001 · операция
   * 7F3K-92QD-1A2B» и «Скопировать номер». Только вместе с кодом.
   */
  operation?: string;
  /**
   * Тост закрылся без его действия: по времени, крестиком или его вытеснил новый. Так
   * отложенное действие с «Отменить» выполняется, когда отменить уже нельзя (удаление файла
   * выгрузки, contracts/ui.md 002). Вызывается один раз; после «Отменить» — не вызывается.
   */
  onDismiss?: () => void;
}

export interface ToastApi {
  /** Показывает тост и возвращает его номер. */
  show: (text: string, options?: ToastOptions) => number;
  /** Итог действия: «Пользователь перенесён в архив · Отменить». */
  ok: (text: string, options?: Omit<ToastOptions, 'kind'>) => number;
  /** Ошибка с «Повторить»: «Изменения не сохранены. Нет связи · Повторить». */
  error: (text: string, options?: Omit<ToastOptions, 'kind'>) => number;
  dismiss: (id: number) => void;
}

export const ToastContext = createContext<ToastApi | null>(null);

export function useToast(): ToastApi {
  const api = useContext(ToastContext);
  if (!api) throw new Error('useToast() — только внутри <ToastProvider>');
  return api;
}

/** Тосты, если есть ToastProvider; без него — null (компонент на отдельной странице, в тесте). */
export function useOptionalToast(): ToastApi | null {
  return useContext(ToastContext);
}

/**
 * Живая область тостов внутри слоя: окна, боковой панели, палитры (ToastLayerRegion). Пока слой
 * открыт, новые тосты озвучиваются в его области: окно прячет от диктора всё вокруг себя
 * (aria-modal, hideOthers Radix), а область внутри окна остаётся слышна.
 */
export interface ToastLiveElements {
  /** Итоги и сведения: вежливо, как role="status". */
  polite: HTMLElement;
  /** Ошибки: настойчиво, как role="alert". */
  assertive: HTMLElement;
}

export interface ToastLayers {
  /** Слой открылся; возвращает отписку — слой закрылся. */
  register: (elements: ToastLiveElements) => () => void;
}

export const ToastLayersContext = createContext<ToastLayers | null>(null);
